// ============================================================================
// GumaHUB i18n Configuration
// Multi-language support using i18next
// ----------------------------------------------------------------------------
// Locale resources load automatically from ../locales/<lng>/<ns>.json via Vite's
// import.meta.glob (eager). Every language folder and every namespace file is
// wired in with zero manual imports — drop a JSON file in and it's picked up on
// the next build. Keep NAMESPACES and SUPPORTED_LANGUAGES in sync with reality.
// ============================================================================

import i18n, { type Resource } from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Eagerly import every locale namespace file: ../locales/<lng>/<ns>.json
const localeModules = import.meta.glob('../locales/*/*.json', { eager: true }) as Record<
  string,
  { default: Record<string, unknown> }
>;

const resources: Record<string, Record<string, unknown>> = {};
for (const path in localeModules) {
  const match = path.match(/\/locales\/([^/]+)\/([^/]+)\.json$/);
  if (!match) continue;
  const [, lng, ns] = match;
  (resources[lng] ??= {})[ns] = localeModules[path].default ?? (localeModules[path] as unknown as Record<string, unknown>);
}

// All namespaces (filenames without .json) — used for the i18next `ns` list.
const NAMESPACES = [
  'common', 'auth', 'dashboard', 'enterprise', 'reference', 'orders', 'reports',
  'shipments', 'settings', 'absence', 'travel', 'expense', 'training', 'documents',
  'service',
];

// Supported languages (order drives the language switcher UI).
export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', flag: '🇹🇷' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'ro', name: 'Romanian', nativeName: 'Română', flag: '🇷🇴' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski', flag: '🇵🇱' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', flag: '🇳🇱' },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska', flag: '🇸🇪' },
  { code: 'da', name: 'Danish', nativeName: 'Dansk', flag: '🇩🇰' },
  { code: 'no', name: 'Norwegian', nativeName: 'Norsk', flag: '🇳🇴' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
];

// Initialize i18next synchronously
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init(
    {
      resources: resources as Resource,
      fallbackLng: 'en',
      defaultNS: 'common',
      ns: NAMESPACES,

      detection: {
        order: ['localStorage', 'navigator'],
        caches: ['localStorage'],
        lookupLocalStorage: 'gumahub-language',
      },

      interpolation: {
        escapeValue: false, // React already escapes values
      },

      react: {
        useSuspense: false,
      },

      // Initialize immediately - CRITICAL for react-i18next binding
      initImmediate: true,
    },
    (err) => {
      if (err) {
        console.error('i18n initialization error:', err);
      } else {
        console.log('i18n initialized successfully');
      }
    }
  );

// Create a promise that resolves immediately since we initialize synchronously
export const i18nReady = Promise.resolve(i18n);

export default i18n;

// Helper to change language
export const changeLanguage = (lang: string) => {
  i18n.changeLanguage(lang);
  localStorage.setItem('gumahub-language', lang);
};

// Get current language
export const getCurrentLanguage = () => i18n.language;
